import { Controller } from "@hotwired/stimulus"
import { useStore } from "stimulus-store"
import { allPartsStore } from "../../stores/parts/all_parts_store"
import { getCsrfToken } from "shared/form_helpers"
import { sendFailureNotification, sendSuccessNotification } from "shared/toast_notifications"

// Connects to data-controller="table--parts-table"
export default class extends Controller {

    static stores = [
        allPartsStore
    ]

    static targets = [
        "table"
    ]

    connect() {
        useStore(this)

        // Defer so argus--table connects first
        Promise.resolve().then(() => {
            this.initializeTable()
        })
    }

    /**
     * React to changes in the all parts store.
     *
     * Normally a store change means the full parts list changed (initial load,
     * create/edit from the parts form) and the table should reload. The delete
     * path, however, mutates exactly one row via argus--table#removeRow and only
     * updates the store to keep it authoritative for other consumers (the parts
     * form reads allPartsStoreValue). In that case we skip the full-list reload
     * so surviving rows keep their control nodes — the row is already gone from
     * the DOM via removeRow. (Requirements 16.1–16.4, 20.1–20.3)
     */
    onAllPartsStoreUpdate() {
        if (this._skipNextStoreReload) {
            this._skipNextStoreReload = false
            return
        }
        this.updateTableData()
    }

    /**
     * Get the argus--table controller instance
     */
    _getArgusTable() {
        if (!this.hasTableTarget) return null
        return this.application.getControllerForElementAndIdentifier(
            this.tableTarget, 'argus--table'
        )
    }

    /**
     * Initialize the table via argus--table
     */
    initializeTable() {
        if (!this.hasTableTarget) return

        const argusTable = this._getArgusTable()
        if (!argusTable) {
            console.warn('table--parts-table: argus--table controller not found')
            return
        }

        // Set up columns programmatically
        argusTable._columns = [
            { field: 'id', title: 'ID', sortable: false, visible: false },
            { field: 'name', title: 'Name', sortable: true, visible: true, width: '50%' },
            { field: 'cost', title: 'Cost', sortable: true, visible: true, width: '30%' },
            { field: 'actions', title: 'Actions', sortable: false, visible: true, width: '20%', formatter: 'partsActionsFormatter' }
        ]

        // Rebuild column toggler
        const ColumnTogglerClass = argusTable.columnToggler.constructor
        argusTable.columnToggler = new ColumnTogglerClass(
            argusTable._columns, () => argusTable._render()
        )

        // Set up custom Add button
        argusTable.buttonsValue = [
            {
                html: '<button type="button" class="btn btn-secondary"><i class="bi bi-plus-lg"></i> Add Part</button>',
                event: 'partsTableAddButtonClick'
            }
        ]

        // Set row attributes
        argusTable._rowAttributesFn = (row, index) => {
            return {
                'data-part-id': row.id,
                'style': 'cursor: pointer;'
            }
        }

        // Load initial data
        const initialData = this.prepareTableData()
        argusTable.loadData(initialData)
    }

    /**
     * Prepare table data from all parts store
     */
    prepareTableData() {
        const parts = this.allPartsStoreValue || []
        return parts.map(part => ({
            id: part.id,
            name: part.name || '',
            cost: part.cost || ''
        }))
    }

    /**
     * Update table data when store changes
     */
    updateTableData() {
        if (!this.hasTableTarget) return

        const argusTable = this._getArgusTable()
        if (!argusTable) return

        const newData = this.prepareTableData()
        argusTable.loadData(newData)
    }

    /**
     * Manually refresh the table
     */
    refresh() {
        this.updateTableData()
    }

    /**
     * Clear the table
     */
    clear() {
        const argusTable = this._getArgusTable()
        if (argusTable) argusTable.clearData()
    }

    /**
     * Get current table data
     */
    getData() {
        const argusTable = this._getArgusTable()
        return argusTable ? argusTable.getData() : []
    }

    /**
     * Handle add button click
     */
    handleAddButtonClick(event) {
        window.dispatchEvent(new Event('partsTableAddButtonClick', {
            detail: {},
            bubbles: true
        }))
    }

    /**
     * Handle edit part action
     */
    handleEditPart(event) {
        event.preventDefault()

        const partId = event.currentTarget.dataset.partId

        window.dispatchEvent(new CustomEvent('partsTableEditButtonClick', {
            detail: { partId: partId },
            bubbles: true
        }))
    }

    /**
     * Handle delete part action
     */
    async handleDeletePart(event) {
        event.preventDefault()

        const partId = event.currentTarget.dataset.partId
        const partName = event.currentTarget.dataset.partName

        const confirmationMessage = `Are you sure you want to delete the part "${ partName }"?`

        if (!confirm(confirmationMessage)) {
            return
        }

        try {
            const response = await fetch('/api/device_types/part/delete', {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                },
                body: JSON.stringify({
                    id: partId,
                    name: partName
                })
            })

            if (response.ok) {
                // Remove exactly this row from the DOM via the shared targeted
                // op so surviving rows keep their control nodes (no full
                // re-render / loadData). (Req 16.1–16.4, 20.1–20.3)
                this._getArgusTable()?.removeRow('id', partId)

                // Keep allPartsStore authoritative for other consumers (the
                // parts form reads allPartsStoreValue on create/edit), but do
                // NOT let this update re-feed the table via loadData — guard the
                // self-reload so only the single removeRow repaints this table.
                this._skipNextStoreReload = true
                const updatedParts = this.allPartsStoreValue.filter(part => part.id !== partId)
                this.setAllPartsStoreValue(updatedParts)

                sendSuccessNotification(`Successfully deleted part "${partName}"`)
            } else {
                const errorData = await response.json()
                sendFailureNotification(`Failed to delete part: ${errorData.message || 'Unknown error'}`)
            }
        } catch (error) {
            console.error('Delete error:', error)
            sendFailureNotification(`Unable to delete part: ${error.message}`)
        }
    }
}
